import { useState } from "react";
import { toast } from "sonner";
import { Loader2, UploadCloud, FileText, X, ArrowRight, ArrowLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { PROFESSIONS, MEDICAL_SPECIALTIES, EXPERIENCE_LEVELS, LANGUAGE_OPTIONS, REGISTRATION_BODIES } from "../../data/site";
import { Field, GdprConsent, FormSuccess, inputCls } from "../forms/FormBits";
import StepBar from "./StepBar";

const STEPS = ["Personal", "Professional", "Compliance"];
const ALLOWED = ["pdf", "doc", "docx"];

const initial = {
  full_name: "", email: "", phone: "", location: "",
  job_title: "", profession: PROFESSIONS[0], medical_specialty: MEDICAL_SPECIALTIES[0],
  years_experience: EXPERIENCE_LEVELS[0], education: "", languages: ["English"], skills: "",
  nmbi_status: "", gmc_status: "", hcpc_status: "", visa_status: "", right_to_work: "",
  document_expiry: "", candidate_source: "Website",
  gdpr_consent: false,
};

export const CandidateIntake = () => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initial);
  const [cv, setCv] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleLang = (l) =>
    setForm((f) => ({ ...f, languages: f.languages.includes(l) ? f.languages.filter((x) => x !== l) : [...f.languages, l] }));

  const uploadCv = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!ALLOWED.includes(ext)) { setErrors((e) => ({ ...e, cv: "CV must be PDF, DOC or DOCX." })); return; }
    if (file.size > 10 * 1024 * 1024) { setErrors((e) => ({ ...e, cv: "File must be under 10MB." })); return; }
    setErrors((e) => ({ ...e, cv: undefined }));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", "cv");
      const { data } = await api.post("/intake/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCv(data);
      toast.success("CV uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const validateStep = () => {
    const e = {};
    if (step === 0) {
      if (form.full_name.trim().length < 2) e.full_name = "Please enter your full name.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email.";
      if (form.phone.trim().length < 5) e.phone = "Enter a contact number.";
    }
    if (step === 2 && !form.gdpr_consent) e.gdpr_consent = "GDPR consent is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (!validateStep()) return;
    setLoading(true);
    try {
      const payload = {
        ...form,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
        cv: cv ? { file_id: cv.file_id, original_filename: cv.original_filename, doc_type: "cv" } : null,
        documents: [],
      };
      await api.post("/intake/candidate", payload);
      setDone(true);
      toast.success("Profile submitted securely");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <FormSuccess
        title="Your professional profile is secured"
        message="Your details and documents have been added to our Candidate Intelligence Database. Our recruitment team will review your profile and be in touch about suitable opportunities."
        onReset={() => { setForm(initial); setCv(null); setStep(0); setDone(false); }}
        resetLabel="Submit another profile"
      />
    );

  return (
    <div data-testid="candidate-intake">
      <StepBar steps={STEPS} current={step} />

      {step === 0 && (
        <div className="space-y-5">
          <Field label="Upload your CV (PDF, DOC, DOCX)" error={errors.cv}>
            {!cv ? (
              <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border bg-secondary/40 px-6 py-7 text-center hover:border-navy/40" data-testid="intake-cv-dropzone">
                {uploading ? <Loader2 className="h-7 w-7 animate-spin text-navy" /> : <UploadCloud className="h-7 w-7 text-navy/50" />}
                <span className="mt-2 text-sm font-medium text-charcoal">{uploading ? "Uploading…" : "Click to upload your CV"}</span>
                <span className="mt-1 text-xs text-muted-foreground">Optional now — you can also submit and send it later</span>
                <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => uploadCv(e.target.files?.[0])} data-testid="intake-cv-input" />
              </label>
            ) : (
              <div className="flex items-center justify-between rounded-lg border border-border bg-white px-4 py-3" data-testid="intake-cv-file">
                <span className="flex items-center gap-2 text-sm text-charcoal"><FileText className="h-4 w-4 text-navy" /> {cv.original_filename}</span>
                <button type="button" onClick={() => setCv(null)} className="text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></button>
              </div>
            )}
          </Field>
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Full name" required error={errors.full_name}><input className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="intake-name" /></Field>
            <Field label="Email" required error={errors.email}><input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="intake-email" /></Field>
            <Field label="Phone" required error={errors.phone}><input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="intake-phone" /></Field>
            <Field label="Location"><input className={inputCls} value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="City, Country" data-testid="intake-location" /></Field>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Current job title"><input className={inputCls} value={form.job_title} onChange={(e) => set("job_title", e.target.value)} data-testid="intake-jobtitle" /></Field>
          <Field label="Profession" required>
            <select className={inputCls} value={form.profession} onChange={(e) => set("profession", e.target.value)} data-testid="intake-profession">{PROFESSIONS.map((o) => <option key={o}>{o}</option>)}</select>
          </Field>
          <Field label="Medical specialty">
            <select className={inputCls} value={form.medical_specialty} onChange={(e) => set("medical_specialty", e.target.value)} data-testid="intake-specialty">{MEDICAL_SPECIALTIES.map((o) => <option key={o}>{o}</option>)}</select>
          </Field>
          <Field label="Years of experience">
            <select className={inputCls} value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} data-testid="intake-experience">{EXPERIENCE_LEVELS.map((o) => <option key={o}>{o}</option>)}</select>
          </Field>
          <Field label="Education / qualifications"><input className={inputCls} value={form.education} onChange={(e) => set("education", e.target.value)} data-testid="intake-education" /></Field>
          <Field label="Key skills (comma separated)"><input className={inputCls} value={form.skills} onChange={(e) => set("skills", e.target.value)} placeholder="e.g. ICU, Ventilation, Triage" data-testid="intake-skills" /></Field>
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-charcoal">Languages</label>
            <div className="flex flex-wrap gap-2">
              {LANGUAGE_OPTIONS.map((l) => (
                <button type="button" key={l} onClick={() => toggleLang(l)} className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${form.languages.includes(l) ? "border-navy bg-navy text-white" : "border-border text-charcoal/70 hover:border-navy/40"}`} data-testid={`intake-lang-${l}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="NMBI status (Ireland)"><input className={inputCls} value={form.nmbi_status} onChange={(e) => set("nmbi_status", e.target.value)} placeholder="e.g. Registered / In progress" data-testid="intake-nmbi" /></Field>
            <Field label="NMC / GMC status (UK)"><input className={inputCls} value={form.gmc_status} onChange={(e) => set("gmc_status", e.target.value)} data-testid="intake-gmc" /></Field>
            <Field label="HCPC / CORU status"><input className={inputCls} value={form.hcpc_status} onChange={(e) => set("hcpc_status", e.target.value)} data-testid="intake-hcpc" /></Field>
            <Field label="Visa status"><input className={inputCls} value={form.visa_status} onChange={(e) => set("visa_status", e.target.value)} data-testid="intake-visa" /></Field>
            <Field label="Right to work"><input className={inputCls} value={form.right_to_work} onChange={(e) => set("right_to_work", e.target.value)} placeholder="e.g. EU Citizen, requires sponsorship" data-testid="intake-rtw" /></Field>
            <Field label="Key document expiry (if any)"><input className={inputCls} value={form.document_expiry} onChange={(e) => set("document_expiry", e.target.value)} placeholder="e.g. Passport 2029, IELTS 2027" data-testid="intake-expiry" /></Field>
          </div>
          <GdprConsent checked={form.gdpr_consent} onChange={(v) => set("gdpr_consent", v)} error={errors.gdpr_consent} />
        </div>
      )}

      <div className="mt-8 flex items-center justify-between">
        <button type="button" onClick={back} disabled={step === 0} className="inline-flex items-center gap-2 rounded-lg border border-border px-5 py-2.5 text-sm font-semibold text-charcoal disabled:opacity-40" data-testid="intake-back">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        {step < STEPS.length - 1 ? (
          <button type="button" onClick={next} className="inline-flex items-center gap-2 rounded-lg bg-navy px-6 py-2.5 text-sm font-semibold text-white hover:bg-navy-light" data-testid="intake-next">
            Continue <ArrowRight className="h-4 w-4" />
          </button>
        ) : (
          <button type="button" onClick={submit} disabled={loading} className="inline-flex items-center gap-2 rounded-lg bg-gold px-6 py-2.5 text-sm font-semibold text-white hover:bg-gold-dark disabled:opacity-70" data-testid="intake-submit">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Submit Profile
          </button>
        )}
      </div>
    </div>
  );
};

export default CandidateIntake;
